import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useUnreadCount } from '../../hooks/useNotifications';
import NotificationCenter from './NotificationCenter';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';

const NotificationBell: React.FC = () => {
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { count: unreadCount } = useUnreadCount();

  const close = useCallback(() => setOpen(false), []);

  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPanelStyle({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
      ...zStyle(Z_INDEX.portaledDropdown),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return;
    }
    updatePosition();
    const onLayout = () => updatePosition();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'relative flex items-center justify-center rounded-lg p-2 transition-colors',
          colors.textMuted,
          `${colors.hoverTextSecondary}`,
          'hover:bg-black/5 dark:hover:bg-white/5',
          'focus:outline-none focus:ring-2 focus:ring-indigo-500',
        )}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && panelStyle && typeof document !== 'undefined'
        ? createPortal(
            <>
              <div
                className="fixed inset-0"
                style={zStyle(Z_INDEX.portaledDropdown - 1)}
                aria-hidden
                onClick={close}
              />
              <div className="fixed w-96 max-w-[calc(100vw-1rem)]" style={panelStyle}>
                <NotificationCenter onClose={close} />
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
};

export default NotificationBell;
